import {createAction, ActionCreator} from '@packages/utilities/create-action';
import type {ListConnectionDef} from '@packages/systems/dynamo/source-types';
import {
  BINDING_CONTEXT_CHANGED,
  DYN_QUERY_FILTER_FOR_CHANGED,
  DYN_QUERY_PARAM_ADDED,
  DYN_QUERY_PARAM_REMOVED,
  DYN_QUERY_PARAM_CHANGED,
  DYN_QUERY_SORT_ADDED,
  DYN_QUERY_SORT_REMOVED,
  DYN_QUERY_SORT_CHANGED,
  DYN_QUERY_SORT_MOVED,
  DYN_LIST_UI_STATE_CHANGED,
  DYN_LIST_UI_STATE_REMOVED,
  DYN_LIST_GRID_LAYOUT_CHANGED,
  DYN_LIST_OFFSET_CHANGED,
  DYN_LIST_OFFSET_LIMIT_CHANGED,
  DYN_LIST_LIMIT_CHANGED,
  DYN_USE_PRIMARY_LOCALE_CHANGED,
  PAGINATION_ENABLED,
  PAGINATION_DISABLED,
  DYN_LIST_SETTINGS_ANALYTICS_EVENT_OCCURRED,
  DYN_LIST_QUERY_MODE_CHANGED,
} from '@packages/systems/designer/actions';
import {
  QUERY_FILTER_FOR_STATES,
  CollectionListQueryMode,
} from '@packages/systems/dynamo/constants';
import {OperatorId} from '@packages/systems/dynamo/utils/DynQueryUtils';

export const bindingContextChanged: ActionCreator<
  typeof BINDING_CONTEXT_CHANGED,
  {
    connection: ListConnectionDef | null;
  }
> = createAction(BINDING_CONTEXT_CHANGED);

export const dynQueryFilterForChanged: ActionCreator<
  typeof DYN_QUERY_FILTER_FOR_CHANGED,
  {
    nodeId: string;
    value: (typeof QUERY_FILTER_FOR_STATES)[keyof typeof QUERY_FILTER_FOR_STATES];
  }
> = createAction(DYN_QUERY_FILTER_FOR_CHANGED);

export const dynQueryParamAdded: ActionCreator<
  typeof DYN_QUERY_PARAM_ADDED,
  {
    nodeId: string;
    fieldPath: string;
    operator: OperatorId;
    value: string | number | null;
    value2: string | number | null;
    value3: string | number | null;
    fieldType: string;
  }
> = createAction(DYN_QUERY_PARAM_ADDED);

export const dynQueryParamChanged: ActionCreator<
  typeof DYN_QUERY_PARAM_CHANGED,
  {
    nodeId: string;
    operator: OperatorId;
    value: string | number | null;
    value2: string | number | null;
    value3: string | number | null;
    fieldType: string;
    fieldPath: string;
    index: number;
    shouldUpdateValue?: boolean;
  }
> = createAction(DYN_QUERY_PARAM_CHANGED);

export const dynQueryParamRemoved: ActionCreator<
  typeof DYN_QUERY_PARAM_REMOVED,
  {
    nodeId: string;
    index: number;
  }
> = createAction(DYN_QUERY_PARAM_REMOVED);

export const dynQuerySortAdded: ActionCreator<
  typeof DYN_QUERY_SORT_ADDED,
  {
    nodeId: string;
    property: string;
    ascending: boolean;
  }
> = createAction(DYN_QUERY_SORT_ADDED);

export const dynQuerySortRemoved: ActionCreator<
  typeof DYN_QUERY_SORT_REMOVED,
  {
    nodeId: string;
    index: number;
  }
> = createAction(DYN_QUERY_SORT_REMOVED);

export const dynQuerySortChanged: ActionCreator<
  typeof DYN_QUERY_SORT_CHANGED,
  {
    nodeId: string;
    index: number;
    property: string;
    ascending: boolean;
  }
> = createAction(DYN_QUERY_SORT_CHANGED);

export const dynQuerySortMoved: ActionCreator<
  typeof DYN_QUERY_SORT_MOVED,
  {
    nodeId: string;
    startIndex: number;
    endIndex: number;
  }
> = createAction(DYN_QUERY_SORT_MOVED);

export const dynListUiStateChanged: ActionCreator<
  typeof DYN_LIST_UI_STATE_CHANGED,
  'items' | 'empty'
> = createAction(DYN_LIST_UI_STATE_CHANGED);

export const dynListUiStateRemoved: ActionCreator<
  typeof DYN_LIST_UI_STATE_REMOVED,
  Record<string, never>
> = createAction(DYN_LIST_UI_STATE_REMOVED);

export const dynListGridLayoutChanged: ActionCreator<
  typeof DYN_LIST_GRID_LAYOUT_CHANGED,
  {
    elementId: string;
    gridLayout: number;
  }
> = createAction(DYN_LIST_GRID_LAYOUT_CHANGED);

export const dynListOffsetChanged: ActionCreator<
  typeof DYN_LIST_OFFSET_CHANGED,
  {
    elementId: string;
    offset: number;
  }
> = createAction(DYN_LIST_OFFSET_CHANGED);

export const dynListOffsetLimitChanged: ActionCreator<
  typeof DYN_LIST_OFFSET_LIMIT_CHANGED,
  {
    selectedNodeNativeId: string | null;
  }
> = createAction(DYN_LIST_OFFSET_LIMIT_CHANGED);

export const dynListLimitChanged: ActionCreator<
  typeof DYN_LIST_LIMIT_CHANGED,
  {
    nodeId: string;
    limit: number | null;
  }
> = createAction(DYN_LIST_LIMIT_CHANGED);

export const dynUsePrimaryLocaleChanged: ActionCreator<
  typeof DYN_USE_PRIMARY_LOCALE_CHANGED,
  {
    usePrimaryLocale: boolean;
    localizationKey?: string;
  }
> = createAction(DYN_USE_PRIMARY_LOCALE_CHANGED);

export const paginationEnabled = createAction<typeof PAGINATION_ENABLED, void>(
  PAGINATION_ENABLED
);

export const paginationDisabled = createAction<
  typeof PAGINATION_DISABLED,
  void
>(PAGINATION_DISABLED);

export const dynListSettingsAnalyticsEventOccurred: ActionCreator<
  typeof DYN_LIST_SETTINGS_ANALYTICS_EVENT_OCCURRED,
  {
    collectionId: string | undefined;
    eventName: string;
  }
> = createAction(DYN_LIST_SETTINGS_ANALYTICS_EVENT_OCCURRED);

export const dynListQueryModeChanged: ActionCreator<
  typeof DYN_LIST_QUERY_MODE_CHANGED,
  {
    mode: CollectionListQueryMode;
  }
> = createAction(DYN_LIST_QUERY_MODE_CHANGED);
